import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@components/ui/tabs";
import { UIPageType, VALIDATION_PATTERNS } from "./allData";
import { Button } from "@components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { toast } from "@components/ui/use-toast";
import {
  CheckCheck,
  SquarePen,
  Trash,
  CalendarIcon,
  TriangleAlert,
} from "lucide-react";
import Confirmation from "@components/common/ConfirmDialog";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { format } from "date-fns";
import {
  createConfigPage,
  deleteConfigPage,
  fetchConfigPage,
  fetchLogicPageInfo,
  fetchPluginInfo,
  sendRequest,
} from "./createPluginApi";
import {
  executeWorkflow,
  getWorkflowById,
} from "@pages/CreatePlugin/createPluginApis";
import PageLoader from "@components/loaders/PageLoader";
import InfoTooltip from "@components/common/InfoTooltip";
import FallBackUi from "./FallBackUi";
import { useCustomToast } from "@components/common/CustomToast";
import {
  useMeshPluginContext,
  MeshPluginActions,
} from "@services/providers/MeshPlugin";
import { HELP_DOC_STYLES } from "./sharedHelpStyles";
import CustomSelect from "@components/common/CustomSelect";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { Label } from "@components/ui/label";
import { Switch } from "@components/ui/switch";
import { Calendar } from "@components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import { loadJsonData } from "@services/api/commonApis";

interface UiPreviewProps {
  open: boolean;
  setOpenPreview: React.Dispatch<React.SetStateAction<boolean>>;
  pages: UIPageType[];
  pluginId?: string | null;
  pluginName?: string;
}

// Normalizes truthy/falsy values coming from saved config (which may be
// stored as booleans, or as "true"/"false" strings) into a real boolean.
const toBoolean = (value: any): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return Boolean(value);
};

const NUMBER_ONLY_PATTERN = /^\d+$/;

const UiPreview: React.FC<UiPreviewProps> = ({
  open,
  pages,
  setOpenPreview,
  pluginId,
  pluginName,
}) => {
  const { savedSettings, dispatch, hasLogicPage } = useMeshPluginContext();
  const [configurationData, setConfigurationData] = React.useState<any[]>([]);

  // Fetch plugin info to get plugin name
  const { data: pluginInfo } = useQuery({
    queryKey: ["pluginInfo", pluginId],
    queryFn: () => fetchPluginInfo(pluginId ?? ""),
    enabled: !!pluginId,
    staleTime: Infinity,
  });

  const resolvedPluginName =
    pluginName?.trim() ||
    pluginInfo?.data?.pluginName ||
    pluginInfo?.pluginName ||
    "";
  const meshVersion =
    pluginInfo?.data?.mesh_version ||
    pluginInfo?.data?.meshVersion ||
    pluginInfo?.mesh_version ||
    pluginInfo?.meshVersion ||
    "";
  const isMeshV2 = String(meshVersion).startsWith("2");
  const [isEditing, setIsEditing] = React.useState(false);
  const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
  const [saveChanges, setSaveChanges] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = React.useState<{
    pageUid: string;
    index: number;
  } | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [invalidFieldLabels, setInvalidFieldLabels] = React.useState<string[]>(
    [],
  );
  const [hasSubmitValidationError, setHasSubmitValidationError] =
    React.useState(false);
  const [liveInvalidFieldMessages, setLiveInvalidFieldMessages] =
    React.useState<Record<string, string>>({});
  const [requestData, setRequestData] = React.useState<any>(null);
  const [data, setData] = React.useState<any>({});
  const [config, setConfig] = React.useState<any>({});
  const [dropdownValues, setDropdownValues] = React.useState<
    Record<string, any>
  >({});
  const [dateValues, setDateValues] = React.useState<Record<string, string>>(
    {},
  );
  const [switchValues, setSwitchValues] = React.useState<
    Record<string, boolean>
  >({});
  const [proxyServerSelections, setProxyServerSelections] = React.useState<
    Record<string, string>
  >({});
  const [testConnectionRequest, setTestConnectionRequests] = React.useState<{
    bundle_id: number;
    request_id: number;
    workflow_id?: string;
  }>({
    bundle_id: 0,
    request_id: 0,
    workflow_id: undefined,
  });
  const [selectedRegion, setSelectedRegion] = React.useState("");
  const [selectedCountry, setSelectedCountry] = React.useState("");

  // Load region and country data from JSON
  const { data: regioncountry } = useQuery(["loadJsonData"], () =>
    loadJsonData("/static/ls/js/countries.json"),
  );

  const regionOptions = React.useMemo(() => {
    if (!Array.isArray(regioncountry)) return [];

    const uniqueRegions = [
      ...new Set(regioncountry.map((item) => item.region).filter(Boolean)),
    ];

    return uniqueRegions.map((region) => ({
      label: region,
      value: region,
    }));
  }, [regioncountry]);

  const countryOptions = React.useMemo(() => {
    if (!selectedRegion) return [];

    return regioncountry
      .filter((item: any) => item.region === selectedRegion)
      .map((item: any) => ({
        label: item.name,
        value: item.name,
      }));
  }, [regioncountry, selectedRegion]);

  const { showToast } = useCustomToast();

  const fieldRefs = React.useRef<Record<string, HTMLInputElement | null | any>>(
    {},
  );
  const titleRef = React.useRef<HTMLInputElement | null>(null);
  const fieldValidationState = React.useRef<Record<string, boolean>>({});

  const getFieldTypeDisplayName = (field: any) => {
    const isNumberField =
      field.fieldType === "input_number" ||
      field.fieldType === "number" ||
      field.field_subtype === "input_number" ||
      field.field_subtype === "number";

    if (isNumberField) return "number";

    switch (field.fieldType) {
      case "ip":
        return "IP address";
      case "domain":
        return "domain";
      case "mac":
        return "MAC address";
      case "port":
        return "port";
      case "host_address":
        return "host address";
      case "url":
        return "URL";
      default:
        return "value";
    }
  };

  const queryClient = useQueryClient();

  const setDateFieldValue = (givenName: string, value: string) => {
    setDateValues((prev) => ({
      ...prev,
      [givenName]: value,
    }));

    const refItem = fieldRefs.current[givenName];
    const inputEl = Array.isArray(refItem)
      ? refItem[refItem.length - 1]
      : refItem;

    if (inputEl && "value" in inputEl) {
      inputEl.value = value;
      inputEl.dispatchEvent(new Event("input", { bubbles: true }));
      inputEl.dispatchEvent(new Event("change", { bubbles: true }));
    }

    triggerValidation();
  };

  // Collect all hidden field keys
  const hiddenKeys = [
    "uid",
    "epoch",
    "pluginId",
    "userID",
    "action",
    "pageUid",
  ];

  // fetch all config data

  // Test Connection API request mutation
  const testConnectionMutation = useMutation({
    mutationFn: (payload: any) => sendRequest(payload),

    onMutate: () => {
      setLoading(true);
    },

    onSuccess: (response) => {
      toast({
        title: "Success",
        description: ` Status : ${response?.status}. Test Connection Successful.`,
        variant: "success",
        duration: 3000,
      });
      setLoading(false);
    },

    onError: (err: any) => {
      let errorMessage = "Could not connect.";

      try {
        // Case 1: Server returned a structured error object
        if (err?.response?.data) {
          const data = err.response.data;

          // Some APIs return the message as a stringified JSON
          if (typeof data.message === "string" && data.message.includes("{")) {
            try {
              // Extract JSON part from message (e.g., "Error: 401 - {...}")
              const jsonPart = data.message.split(" - ")[1] || data.message;
              const inner = JSON.parse(jsonPart);

              // Handle different error structures
              if (inner.error) {
                if (typeof inner.error === "string") {
                  errorMessage = inner.error;
                } else if (inner.error.message) {
                  errorMessage = inner.error.message;
                } else if (inner.error.code) {
                  errorMessage = inner.error.code;
                }
              } else if (inner.error_description) {
                errorMessage = inner.error_description;
              } else if (inner.message) {
                errorMessage = inner.message;
              } else {
                // Fallback to original message
                errorMessage = data.message;
              }
            } catch (parseError) {
              // If JSON parsing fails, use the original message
              errorMessage = data.message || "Could not connect.";
            }
          } else {
            // Simple string message
            errorMessage =
              data.error_description || data.message || errorMessage;
          }
        }
        // Case 2: Fallback to plain err.message if available
        else if (err?.message) {
          errorMessage = err.message;
        }
      } catch (outerError) {
        errorMessage = "An unexpected error occurred during test connection.";
      }

      // Safely show toast
      try {
        showToast({
          title: "Test Connection Failed",
          description: errorMessage,
          variant: "destructive",
          duration: 3000,
        });
      } catch (toastError) {
        // Fallback to regular toast
        toast({
          title: "Test Connection Failed",
          description: errorMessage,
          variant: "destructive",
          duration: 3000,
        });
      }

      setLoading(false);
    },
  });

  // Check if we're in edit mode
  const isEditMode = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("edit") === "1";
  }, []);

  // Fetch config page data
  const { data: configPageData, isLoading: isConfigLoading } = useQuery(
    ["configPage", pluginId, "temp"],
    () => fetchConfigPage(pluginId ?? "", "temp"),
    {
      enabled: !!pluginId && open,
    },
  );

  // Fetch logic page info
  const { data: logicPageInfo, isLoading: isLogicLoading } = useQuery({
    queryKey: ["logicPageInfo", pluginId],
    queryFn: () => fetchLogicPageInfo(pluginId ?? ""),
    enabled: !!pluginId && isEditMode && hasLogicPage,
  });

  // Fetch workflow data if it's a workflow-based test connection (mesh 2.0)
  const { data: workflowData, isLoading: isWorkflowLoading } = useQuery({
    queryKey: ["testConnectionWorkflow", testConnectionRequest.workflow_id],
    queryFn: () =>
      getWorkflowById(testConnectionRequest.workflow_id!, pluginId ?? ""),
    enabled: !!testConnectionRequest.workflow_id && !!pluginId,
    staleTime: 0,
  });

  //set up the data state whenever there is any change in the form fields
  React.useEffect(() => {
    const updateAllData = () => {
      const allData: Record<string, any> = {};

      allData["title"] = titleRef.current?.value?.trim() || "";

      settingsPages.forEach((page) => {
        page.forms?.forEach((form) => {
          form.fieldList.forEach((field) => {
            if (
              field.fieldType === "testConnectionBtn" ||
              field.givenName === "title"
            )
              return;

            const key: any = field.givenName;
            let value: any = "";

            if (field.fieldType === "checkbox") {
              const elements = fieldRefs.current[key];
              if (Array.isArray(elements)) {
                value = elements
                  .filter((el: any) => el?.checked)
                  .map((el: any) => el.value);
              }
            } else if (field.fieldType === "radio") {
              value = String(
                dropdownValues[key] ||
                  field.default ||
                  field.values?.[0]?.value ||
                  "",
              );
            } else if (
              field.fieldType === "dropdown" ||
              field.fieldType === "multi_select_dropdown"
            ) {
              value = dropdownValues[key] || "";
            } else if (field.fieldType === "input_date") {
              const item = fieldRefs.current[key];
              const refValue = Array.isArray(item)
                ? item[item.length - 1]?.value || ""
                : item?.value || "";
              value = dateValues[key] || refValue;
            } else {
              const item = fieldRefs.current[key];
              if (Array.isArray(item)) {
                value = item[0]?.value || "";
              } else {
                value = item?.value || "";
              }
            }
            allData[key] = value;
          });
        });
      });

      Object.entries(switchValues).forEach(([key, value]) => {
        allData[key] = value;
      });

      Object.entries(proxyServerSelections).forEach(([key, value]) => {
        allData[`${key}_epochtime`] = value;
      });

      setData(allData);
    };

    const inputs = Object.values(fieldRefs.current || {});

    inputs.forEach((item: any) => {
      const elements = Array.isArray(item) ? item : [item];
      elements.forEach((input) => {
        if (!(input instanceof HTMLElement)) return;
        input.addEventListener("input", updateAllData);
        input.addEventListener("change", updateAllData);
      });
    });

    titleRef.current?.addEventListener("input", updateAllData);

    updateAllData();

    return () => {
      inputs.forEach((item: any) => {
        const elements = Array.isArray(item) ? item : [item];
        elements.forEach((input) => {
          if (!(input instanceof HTMLElement)) return;

          input.removeEventListener("input", updateAllData);
          input.removeEventListener("change", updateAllData);
        });
      });

      titleRef.current?.removeEventListener("input", updateAllData);
    };
  }, [
    pages,
    activeTab,
    isEditing,
    editingIndex,
    fieldRefs.current,
    titleRef.current,
    switchValues,
    proxyServerSelections,
    dateValues,
  ]);

  // setting up the bundleId and requestId from already selected fields
  React.useEffect(() => {
    // Extract test connection button from pages (which is the actual rendered data)
    let testConnectionBtn: any = null;

    for (const page of pages) {
      if (page.pageType === "settings" && page.forms) {
        for (const form of page.forms) {
          const field = form.fieldList.find(
            (f: any) => f.fieldType === "testConnectionBtn",
          );
          if (field) {
            testConnectionBtn = field;
            break;
          }
        }
        if (testConnectionBtn) break;
      }
    }

    if (testConnectionBtn?.bundleId && testConnectionBtn?.requestId) {
      // Check if bundleId is a workflow ID (starts with "workflow_")
      const isWorkflowId =
        typeof testConnectionBtn.bundleId === "string" &&
        testConnectionBtn.bundleId.startsWith("workflow_");

      setTestConnectionRequests({
        bundle_id: isWorkflowId ? 0 : testConnectionBtn.bundleId,
        request_id: isWorkflowId ? 0 : testConnectionBtn.requestId,
        workflow_id: isWorkflowId ? testConnectionBtn.bundleId : undefined,
      });
    } else {
      // Reset if no test connection field found
      setTestConnectionRequests({
        bundle_id: 0,
        request_id: 0,
        workflow_id: undefined,
      });
      setRequestData(null);
    }
  }, [pages, logicPageInfo]);

  // set the requestData
  React.useEffect(() => {
    const allBundles = logicPageInfo?.data?.LogicBundles;
    const requestBundle = allBundles
      ?.flatMap((bundle: any) => bundle)
      .find((field: any) => field);

    const targetRequest = requestBundle?.requests?.find(
      (item: any) => item.requestId == testConnectionRequest?.request_id,
    );

    setRequestData(targetRequest);
  }, [testConnectionRequest, logicPageInfo]);

  // set the requestData to the state if there is any testConnectionBtn field in the logicPageInfo
  // find the request data from logicPageInfo according to bundle_id and request_id
  React.useEffect(() => {
    if (logicPageInfo) {
      const bundle_id = testConnectionRequest.bundle_id;
      const request_id = testConnectionRequest.request_id;

      const allBundles = logicPageInfo.data.LogicBundles;
      const requestBundle = allBundles
        .flatMap((bundle: any) => bundle)
        .find((field: any) => field.bundleId == bundle_id);
      const requestData = requestBundle?.requests.find(
        (field: any) => field.requestId == request_id,
      );
      if (requestBundle) {
        setRequestData(requestData);
      }
    }
  }, [logicPageInfo]);

  // mutation for create and update config page
  const createOrUpdateConfigMutation = useMutation({
    mutationFn: ({
      tab_id,
      data,
      type_page = "temp",
      uid,
    }: {
      tab_id: string;
      data: any[];
      type_page?: string;
      uid?: string | number;
    }) =>
      createConfigPage({
        plugin_id: pluginId ?? "",
        tab_id,
        data,
        type_page,
        uid,
      }),
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: (response) => {
      setLoading(false);
      toast({
        title: "Config saved!",
        description: response?.message || "Config page updated/created.",
        variant: "success",
        duration: 3000,
      });
      queryClient.invalidateQueries(["configPage", pluginId]);
    },
    onError: () => {
      setLoading(false);
      toast({
        title: "Error",
        description: "Failed to save config page.",
        variant: "destructive",
        duration: 3000,
      });
    },
  });

  // Delete config mutation
  const deleteConfigMutation = useMutation({
    mutationFn: ({
      tab_id,
      uid,
      type_page = "temp",
    }: {
      tab_id: string;
      uid: string;
      type_page?: string;
    }) =>
      deleteConfigPage({
        plugin_id: pluginId ?? "",
        tab_id,
        uid,
        type_page,
      }),
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: (response) => {
      setLoading(false);
      toast({
        title: "Deleted!",
        description: response?.message || "Config deleted.",
        variant: "success",
        duration: 3000,
      });
      queryClient.invalidateQueries(["configPage", pluginId]);
    },
    onError: () => {
      setLoading(false);
      toast({
        title: "Error",
        description: "Failed to delete config.",
        variant: "destructive",
        duration: 3000,
      });
    },
  });

  const settingsPages = pages.filter(
    (page) =>
      page.pageType === "settings" && page.forms && page.forms.length > 0,
  );
  const helpPages = pages.filter((page) => page.pageType == "Help");

  // Handle settings form submission
  const handleSettingsSubmit = async (e: any) => {
    e.preventDefault();
    const pageUid = activeTab ?? "";

    if (!validateForm()) {
      setHasSubmitValidationError(true);
      // Find which field types failed validation
      const invalidFields: string[] = [];
      const activePage = settingsPages.find((p) => String(p.uid) === pageUid);
      if (activePage) {
        activePage.forms?.forEach((form) => {
          form.fieldList
            .filter(
              (field: any) =>
                field.fieldType !== "testConnectionBtn" &&
                field.fieldType !== "title" &&
                field.givenName !== "title",
            )
            .forEach((field: any) => {
              const inputEl: any = fieldRefs.current[field.givenName];
              const isNumberField =
                field.fieldType === "input_number" ||
                field.fieldType === "number" ||
                field.field_subtype === "input_number" ||
                field.field_subtype === "number";
              if (
                field.fieldType === "ip" ||
                field.fieldType === "domain" ||
                field.fieldType === "mac" ||
                field.fieldType === "port" ||
                field.fieldType === "host_address" ||
                field.fieldType === "url" ||
                isNumberField
              ) {
                let value = "";
                if (Array.isArray(inputEl)) {
                  value = inputEl[0]?.value?.trim() || "";
                } else if (inputEl) {
                  value = inputEl.value?.trim() || "";
                }

                const isValidNumberField =
                  !isNumberField || NUMBER_ONLY_PATTERN.test(value);

                if (
                  value &&
                  ((isNumberField && !isValidNumberField) ||
                    (!isNumberField &&
                      VALIDATION_PATTERNS[
                        field.fieldType as keyof typeof VALIDATION_PATTERNS
                      ] &&
                      !VALIDATION_PATTERNS[
                        field.fieldType as keyof typeof VALIDATION_PATTERNS
                      ].test(value)))
                ) {
                  const fieldLabel =
                    field.fieldType === "ip"
                      ? "IP Address"
                      : field.fieldType === "domain"
                        ? "Domain"
                        : field.fieldType === "mac"
                          ? "MAC Address"
                          : field.fieldType === "host_address"
                            ? "Host Address"
                            : field.fieldType === "url"
                              ? "URL"
                              : isNumberField
                                ? "Number"
                                : "Port";
                  if (!invalidFields.includes(fieldLabel)) {
                    invalidFields.push(fieldLabel);
                  }
                }
              }

              if (field.fieldType === "region_country") {
                if (!selectedRegion || !selectedCountry) {
                  const fieldLabel = "Region & Country";
                  if (!invalidFields.includes(fieldLabel)) {
                    invalidFields.push(fieldLabel);
                  }
                }
              }
            });
        });
      }

      const errorMessage =
        invalidFields.length > 0
          ? `Invalid format for: ${invalidFields.join(", ")}. Please check and correct.`
          : "Please ensure all fields are filled correctly with valid format.";

      toast({
        variant: "destructive",
        title: "Validation Error: " + invalidFields.join(", "),
        description: errorMessage,
        duration: 3000,
      });
      return;
    }

    setHasSubmitValidationError(false);

    // toast error when same title entered for new settings
    const newTitle = titleRef.current?.value?.trim()?.toLowerCase();

    const duplicateTitle = Object.entries(savedSettings || {}).some(
      ([pageUid, configs]: [string, any[]]) => {
        // Iterate over each config in that page’s array
        return configs.some((config, index) => {
          // Skip the one currently being edited
          const isSameEditingConfig =
            isEditing && pageUid == String(activeTab) && index == editingIndex;

          return (
            !isSameEditingConfig &&
            config.title?.trim()?.toLowerCase() == newTitle
          );
        });
      },
    );

    if (duplicateTitle) {
      toast({
        title: "Duplicate Title",
        description: "A setting with this title already exists.",
        variant: "destructive",
      });
      return;
    }

    const uid = `${Date.now()}`;
    const epoch = `${Date.now()}`;
    const action = "saveCustomSettings";

    if (!pageUid || pageUid === "undefined") {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          "Invalid page. Please select a valid settings page or create a new one.",
        duration: 3000,
      });
      return;
    }

    const baseObject: Record<string, string> = {
      pluginId: pluginId ?? "",
      uid,
      epoch,
      action,
      pageUid,
    };

    const titleValue = titleRef.current?.value?.trim() || "";
    if (!titleValue) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please provide a title.",
        duration: 3000,
      });
      return;
    }
    baseObject["title"] = titleValue;

    // Collect all field values into baseObject for table display
    settingsPages.forEach((page) => {
      page.forms?.forEach((form) => {
        form.fieldList.forEach((field) => {
          if (
            field.fieldType === "testConnectionBtn" ||
            field.givenName === "title"
          )
            return;
          const key: any = field.givenName;
          let val = "";
          if (field.fieldType === "radio" || field.fieldType === "dropdown") {
            val = String(
              dropdownValues[key] ||
                field.default ||
                (field.fieldType === "radio" ? field.values?.[0]?.value : "") ||
                "",
            );
          } else if (field.fieldType === "multi_select_dropdown") {
            const raw = dropdownValues[key];
            val = Array.isArray(raw) ? raw.join(",") : raw || "";
          } else if (field.fieldType === "input_date") {
            const item = fieldRefs.current[key];
            const refValue = Array.isArray(item)
              ? item[item.length - 1]?.value || ""
              : item?.value || "";
            val = dateValues[key] || refValue;
          } else if (field.fieldType === "checkbox") {
            const elements = fieldRefs.current[key];
            if (Array.isArray(elements)) {
              val = elements
                .filter((el: any) => el?.checked)
                .map((el: any) => el.value)
                .join(",");
            }
          } else {
            const item = fieldRefs.current[key];
            val = Array.isArray(item) ? item[0]?.value : item?.value || "";
          }
          if (field.fieldType === "region_country") {
            baseObject[key] = JSON.stringify({
              region: selectedRegion,
              country: selectedCountry,
            });
            return;
          }
          if (field.fieldType === "proxy" || field.givenName === "proxy") {
            baseObject[key] = String(switchValues[key] ?? false);
            if (isMeshV2) {
              baseObject[`${key}_epochtime`] = proxyServerSelections[key] || "";
            }
            return;
          }
          baseObject[key] = val;
        });
      });
    });

    const currentPage = settingsPages.find(
      (page) => String(page.uid) === pageUid,
    );

    if (!currentPage || !currentPage.forms) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Settings page not found or has no forms.",
        duration: 3000,
      });
      return;
    }

    // Collect data for each form on the page
    const formConfigs: {
      value: string;
      givenName: string;
      isEncript: boolean;
    }[] = [];

    // Add title field first
    formConfigs.push({
      value: titleValue,
      givenName: "title",
      isEncript: false,
    });

    currentPage.forms.forEach((form) => {
      form.fieldList.forEach((field) => {
        // Skip testConnectionBtn and title (title is handled separately via titleRef)
        if (
          field.fieldType === "testConnectionBtn" ||
          field.fieldType === "title" ||
          field.givenName === "title"
        )
          return;
        const key = field.givenName;
        if (!key) return;
        let value: any = "";

        if (field.fieldType === "multi_select_dropdown") {
          const raw = dropdownValues[key];
          value = Array.isArray(raw) ? raw.join(",") : raw || "";
        } else if (field.fieldType === "dropdown") {
          value = dropdownValues[key] || "";
        } else if (field.fieldType === "radio") {
          value = String(
            dropdownValues[key] ||
              field.default ||
              field.values?.[0]?.value ||
              "",
          );
        } else if (field.fieldType === "input_date") {
          const item = fieldRefs.current[key];
          const refValue = Array.isArray(item)
            ? item[item.length - 1]?.value || ""
            : item?.value || "";
          value = dateValues[key] || refValue;
        } else if (field.fieldType === "proxy" || field.givenName === "proxy") {
          value = switchValues[key] ?? false;
          if (isMeshV2) {
            formConfigs.push({
              value: proxyServerSelections[key] || "",
              givenName: `${key}_epochtime`,
              isEncript: false,
            });
          }
        } else if (field.fieldType === "checkbox") {
          const elements = fieldRefs.current[key];
          if (Array.isArray(elements)) {
            value = elements
              .filter((el: any) => el?.checked)
              .map((el: any) => el.value)
              .join(",");
          }
        } else if (field.fieldType === "region_country") {
          formConfigs.push({
            value: JSON.stringify({
              region: selectedRegion,
              country: selectedCountry,
            }),
            givenName: key,
            isEncript: false,
          });
          return;
        } else {
          const item = fieldRefs.current[key];
          if (Array.isArray(item)) {
            value = item[0]?.value || "";
          } else {
            value = item?.value || "";
          }
        }

        // No longer checking for missing required fields - all fields are optional
        // Only format validation for IP, MAC, Port, Domain (handled in validateForm)

        formConfigs.push({
          value,
          givenName: key,
          isEncript:
            field.fieldType == "password" ||
            field.fieldType == "input_password" ||
            field.field_subtype == "input_password",
        });
      });
    });

    // Build the payload
    const payload: {
      tab_id: string;
      data: any[];
      type_page?: string;
      uid?: string | number;
    } = {
      tab_id: pageUid,
      data: formConfigs,
      type_page: "temp",
    };

    // If editing, add uid for update
    if (isEditing && editingIndex != null) {
      // Get the uid from the config being edited
      const editingUid = savedSettings?.[pageUid]?.[editingIndex]?.uid;
      if (editingUid) payload.uid = editingUid;
    }
    // Call the mutation
    createOrUpdateConfigMutation.mutate(payload, {
      onSuccess: () => {
        setIsEditing(false);
        setEditingIndex(null);
        clearForm();
        queryClient.invalidateQueries(["configPage", pluginId, "temp"]);
      },
    });
  };

  // Handle edit action
  const handleEdit = (pageUid: string, index: number) => {
    const data = savedSettings?.[pageUid]?.[index];

    if (!data) return;

    // Find matching request data for the page
    if (logicPageInfo?.data?.LogicBundles?.length) {
      const { bundle_id, request_id } = testConnectionRequest;
      const allBundles = logicPageInfo.data.LogicBundles;
      const requestBundle = allBundles
        .flatMap((bundle: any) => bundle)
        .find((bundle: any) => bundle.bundleId == bundle_id);

      if (requestBundle) {
        const foundRequest = requestBundle.requests.find(
          (req: any) => req.requestId == request_id,
        );
        setRequestData(foundRequest);
      }
    }

    titleRef.current && (titleRef.current.value = data.title || "");
    setSelectedRegion("");
    setSelectedCountry("");

    // First, clear all fields to ensure new fields are empty
    Object.values(fieldRefs.current).forEach((item: any) => {
      if (!item) return;
      const elements = Array.isArray(item) ? item : [item];
      elements.forEach((ref: any) => {
        if (ref.type === "checkbox" || ref.type === "radio")
          ref.checked = false;
        else ref.value = "";
      });
    });

    // Clear dropdown values
    const newDropdownValues: Record<string, any> = {};
    const newDateValues: Record<string, string> = {};
    const newSwitchValues: Record<string, boolean> = {};
    const newProxyServerSelections: Record<string, string> = {};
    const currentPage = settingsPages.find(
      (page) => String(page.uid) === pageUid,
    );

    // Seed proxy defaults from form definition so edit mode always shows exact proxy state.
    currentPage?.forms?.forEach((form: any) => {
      form.fieldList.forEach((field: any) => {
        if (field.fieldType === "proxy" || field.givenName === "proxy") {
          const proxyKey = field.givenName;
          newSwitchValues[proxyKey] = toBoolean(
            data[proxyKey] ?? field.default,
          );
        }
      });
    });

    // Then, populate with saved data (this will leave new fields empty)
    Object.keys(data).forEach((key) => {
      const item = fieldRefs.current[key];
      const value = data[key];

      if (key.endsWith("_epochtime")) {
        const proxyKey = key.replace(/_epochtime$/, "");
        newProxyServerSelections[proxyKey] = String(value || "");
        return;
      }

      // Check field definition
      const fieldDef = settingsPages
        .find((page) => String(page.uid) === pageUid)
        ?.forms?.flatMap((form) => form.fieldList)
        .find((field: any) => field.givenName === key);

      const isDropdown = fieldDef?.fieldType === "dropdown";
      const isMultiSelect = fieldDef?.fieldType === "multi_select_dropdown";
      const isRadio = fieldDef?.fieldType === "radio";
      const isRegionCountry = fieldDef?.fieldType === "region_country";
      const isInputDate = fieldDef?.fieldType === "input_date";

      // Region/Country
      if (isRegionCountry) {
        try {
          const parsed = typeof value === "string" ? JSON.parse(value) : value;

          const region = parsed?.region || "";
          const country = parsed?.country || "";

          newDropdownValues[key] = value;
          setSelectedRegion(region);
          setSelectedCountry(country);
        } catch {
          setSelectedRegion("");
          setSelectedCountry("");
        }
        return;
      }

      // Multi Select
      if (isMultiSelect) {
        newDropdownValues[key] =
          typeof value === "string" && value
            ? value.split(",")
            : Array.isArray(value)
              ? value
              : [];
        return;
      }

      // Dropdown
      if (isDropdown) {
        newDropdownValues[key] = value || "";
        return;
      }

      if (isInputDate) {
        newDateValues[key] = String(value || "");

        if (Array.isArray(item)) {
          item.forEach((el: any) => {
            if (el && "value" in el) {
              el.value = String(value || "");
            }
          });
        } else if (item && "value" in item) {
          item.value = String(value || "");
        }

        return;
      }

      // Proxy switch
      if (key === "proxy") {
        newSwitchValues[key] = toBoolean(value);
        return;
      }

      // Radio fields rendered via Custom Components
      if (isRadio) {
        newDropdownValues[key] = value || "";
      }

      // Multiple refs (checkbox/radio groups)
      if (Array.isArray(item)) {
        const values = Array.isArray(value)
          ? value
          : typeof value === "string"
            ? value.split(",")
            : [];

        item.forEach((el: any) => {
          if (el.type === "checkbox") {
            el.checked = values.includes(el.value);
          } else if (el.type === "radio") {
            el.checked = el.value === value;
          } else {
            el.value = value || "";
          }
        });

        return;
      }

      // Single refs
      if (item) {
        if (item.type === "checkbox") {
          item.checked = toBoolean(value);
        } else if (item.type === "radio") {
          item.checked = item.value === value;

          // Fallback in case the ref isn't the checked radio
          const radio = document.querySelector(
            `input[name="${key}"][value="${value}"]`,
          ) as HTMLInputElement | null;

          if (radio) {
            radio.checked = true;
          }
        } else {
          item.value = value || "";
        }
      }
    });

    setDropdownValues(newDropdownValues);
    setDateValues(newDateValues);
    setSwitchValues(newSwitchValues);
    setProxyServerSelections(newProxyServerSelections);
    setEditingIndex(index);
    setIsEditing(true);
    triggerValidation();

    // Immediately update allData/data after edit
    const allData: Record<string, any> = {};
    allData["title"] = titleRef.current?.value?.trim() || "";

    // Add dropdown values
    Object.entries(newDropdownValues).forEach(([key, value]) => {
      const fieldDef = settingsPages
        .find((page) => String(page.uid) === pageUid)
        ?.forms?.flatMap((form) => form.fieldList)
        .find((field: any) => field.givenName === key);

      if (fieldDef?.fieldType === "region_country") {
        allData[key] = value;
      } else {
        allData[key] = value;
      }
    });

    Object.entries(newSwitchValues).forEach(([key, value]) => {
      allData[key] = value;
    });

    Object.entries(newProxyServerSelections).forEach(([key, value]) => {
      allData[`${key}_epochtime`] = value;
    });

    Object.entries(fieldRefs.current).forEach(([key, ref]) => {
      if (!ref) return;
      if (ref.type === "checkbox") {
        const checkboxes = document.querySelectorAll(
          `input[name="${key}"]:checked`,
        );

        allData[key] = Array.from(checkboxes).map((el: any) => el.value);
      } else if (ref.type === "radio") {
        const radios = document.getElementsByName(key);
        const checkedRadio = Array.from(radios).find((r: any) => r.checked);
        allData[key] = checkedRadio
          ? (checkedRadio as HTMLInputElement).value
          : "";
      } else {
        allData[key] = ref.value || "";
      }
    });
    setData(allData);

    // Set config for the edited item
    setConfig({
      tabId: pageUid,
      uid: data.uid,
      configType: "temp",
    });
  };

  // Update handleDelete to use mutation
  const handleDelete = (pageUid: string, index: number) => {
    const uid = savedSettings?.[pageUid]?.[index]?.uid;
    if (!uid) {
      toast({
        title: "Error",
        description: "UID not found for deletion.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    deleteConfigMutation.mutate(
      { tab_id: pageUid, uid, type_page: "temp" },
      {
        onSuccess: () => {
          // Only update state after successful deletion
          dispatch({
            type: MeshPluginActions.SET_SAVED_SETTINGS,
            payload: (prev: Record<string, any[]>) => {
              const current = prev[pageUid] ?? [];
              const filtered = current.filter(
                (_: any, i: number) => i !== index,
              );
              return { ...prev, [pageUid]: filtered };
            },
          });
          if (editingIndex === index) {
            clearForm();
            setIsEditing(false);
            setEditingIndex(null);
          }
          setConfigurationData?.((prev: any[]) => {
            const existingPage = prev.find(
              (entry) => entry.pageUid === pageUid,
            );
            if (existingPage) {
              const updatedData: any = existingPage.data.filter(
                (_: any, i: any) => i !== index,
              );
              return prev?.map((entry) =>
                entry.pageUid === pageUid
                  ? { ...entry, data: updatedData }
                  : entry,
              );
            }
            return prev;
          });
        },
      },
    );
  };

  const triggerValidation = () => {
    setIsFormValid(validateForm());
  };

  // Check if all fields are empty
  const areAllFieldsEmpty = () => {
    // Check title field
    if (titleRef.current && titleRef.current.value.trim()) {
      return false;
    }

    // Check all other fields
    const allEmpty = Object.values(fieldRefs.current).every((ref) => {
      if (!ref) return true;
      const elements = Array.isArray(ref) ? ref : [ref];
      return elements.every((el) => {
        if (el.type === "checkbox" || el.type === "radio") {
          return !el.checked;
        }
        return !el.value || el.value.trim() === "";
      });
    });

    return allEmpty;
  };

  // Check if all fields are filled (for Test Connection button)
  const areAllFieldsFilled = () => {
    // Check title field
    const title = titleRef.current?.value?.trim();
    if (!title) return false;

    // Only check fields for the current active page
    const currentPage = settingsPages.find(
      (page) => String(page.uid) === activeTab,
    );
    if (!currentPage) return false;

    return currentPage.forms?.every((form) =>
      form.fieldList
        .filter(
          (field: any) =>
            field.fieldType !== "testConnectionBtn" &&
            field.fieldType !== "title" &&
            field.givenName !== "title",
        )
        .every((field: any) => {
          // Dropdowns and Radios (now using dropdownValues)
          if (
            field.fieldType === "dropdown" ||
            field.fieldType === "radio" ||
            field.fieldType === "multi_select_dropdown"
          ) {
            const value = dropdownValues[field.givenName];
            return value !== undefined && value !== "" && value !== null;
          }
          if (field.fieldType === "proxy" || field.givenName === "proxy") {
            return true;
          }

          if (field.fieldType === "region_country") {
            const isRegionSelected = Array.isArray(selectedRegion)
              ? selectedRegion.length > 0
              : !!selectedRegion;

            const isCountrySelected = Array.isArray(selectedCountry)
              ? selectedCountry.length > 0
              : !!selectedCountry;

            return isRegionSelected && isCountrySelected;
          }

          if (field.fieldType === "input_date") {
            const refItem = fieldRefs.current[field.givenName];
            const hiddenValue = Array.isArray(refItem)
              ? refItem[0]?.value?.trim() || ""
              : refItem?.value?.trim() || "";
            const selectedDate = (dateValues[field.givenName] || "").trim();
            return !!(selectedDate || hiddenValue);
          }

          const inputEl = fieldRefs.current[field.givenName];
          if (!inputEl) return false;

          // Checkboxes are always considered filled (can be checked or unchecked)
          if (field.fieldType === "checkbox") {
            return true;
          }

          // All other fields must have a value
          let value = "";
          if (Array.isArray(inputEl)) {
            value = inputEl[0]?.value?.trim() || "";
          } else {
            value = inputEl.value?.trim() || "";
          }
          return value !== "";
        }),
    );
  };

  // Clear form fields
  const clearForm = () => {
    titleRef.current && (titleRef.current.value = "");
    Object.values(fieldRefs.current).forEach((item: any) => {
      if (!item) return;
      const elements = Array.isArray(item) ? item : [item];
      elements.forEach((ref: any) => {
        if (ref.type === "checkbox" || ref.type === "radio")
          ref.checked = false;
        else ref.value = "";
      });
    });
    const clearedDropdowns: Record<string, any> = {};

    settingsPages.forEach((page) => {
      page.forms?.forEach((form) => {
        form.fieldList.forEach((field: any) => {
          if (field.fieldType === "dropdown") {
            clearedDropdowns[field.givenName] = "select";
          }

          if (field.fieldType === "multi_select_dropdown") {
            clearedDropdowns[field.givenName] = [];
          }

          if (field.fieldType === "radio") {
            clearedDropdowns[field.givenName] = "";
          }
        });
      });
    });

    setDropdownValues(clearedDropdowns);
    setDateValues({});
    setSwitchValues({});
    setProxyServerSelections({});

    setIsEditing(false);
    setEditingIndex(null);
    setHasSubmitValidationError(false);
    setLiveInvalidFieldMessages({});
    triggerValidation();
    setSelectedRegion("");
    setSelectedCountry("");
    setData({});
    setConfig({});
  };

  // Handle dialog close
  const handleClose = () => {
    if (isEditing) {
      setSaveChanges(true);
      return;
    }
    clearForm();
    setIsEditing(false);
    setEditingIndex(null);
    setOpenPreview(false);
  };

  // const handleClosePreview = () => {
  //   if (isEditing) {
  //     setSaveChanges(true);
  //   } else {
  //     handleClose();
  //   }
  // };

  // to show the column name as value if its isDecript or similar
  const filterConfigKeys = (config: any) => {
    const excludeKeys = ["isDecript", "testConnectionBtn"];
    return Object.fromEntries(
      Object.entries(config).filter(([key]) => !excludeKeys.includes(key)),
    );
  };

  // Update savedSettings and configurationData when configPageData changes
  React.useEffect(() => {
    if (!configPageData?.data) return;

    const newSavedSettings: Record<string, any[]> = {};
    const newConfigData: any[] = [];

    Object.entries(configPageData.data).forEach(([pageUid, configs]) => {
      const filteredConfigs = (configs as any[]).map(filterConfigKeys);
      newSavedSettings[pageUid] = filteredConfigs;
      newConfigData.push({ pageUid, data: filteredConfigs });
    });

    dispatch({
      type: MeshPluginActions.SET_SAVED_SETTINGS,
      payload: (prev: Record<string, any[]>) => ({
        ...prev,
        ...newSavedSettings,
      }),
    });
    setConfigurationData(newConfigData);
  }, [configPageData, dispatch]);

  // Set initial active tab on pages change or when dialog opens
  React.useEffect(() => {
    if (open) {
      const firstSettingsPage = settingsPages[0];
      const firstHelpPage = helpPages[0];

      if (firstSettingsPage) {
        setActiveTab(String(firstSettingsPage.uid));
      } else if (firstHelpPage) {
        setActiveTab(String(firstHelpPage.uid));
      }
    }
  }, [open, pages]);

  // onClose
  const onDeleteCancel = () => {
    setPendingDelete(null);
  };

  // Confirmation dialog handlers
  const onDeleteConfirm = () => {
    if (pendingDelete) {
      handleDelete(pendingDelete.pageUid, pendingDelete.index);
      setPendingDelete(null);
    }
  };

  const validateForm = () => {
    const invalidFields = new Set<string>();

    const markInvalidField = (field: any, fallback = "Field") => {
      const label =
        String(field?.fieldTitle || "").trim() ||
        String(field?.givenName || "").trim() ||
        fallback;
      invalidFields.add(label);
    };

    // Validate title
    const title = titleRef.current?.value?.trim();
    if (!title) {
      invalidFields.add("Title");
      setInvalidFieldLabels(Array.from(invalidFields));
      return false;
    }

    const validators: Record<string, RegExp> = {
      ip: VALIDATION_PATTERNS.ip,
      domain: VALIDATION_PATTERNS.domain,
      mac: VALIDATION_PATTERNS.mac,
      port: VALIDATION_PATTERNS.port,
      host_address: VALIDATION_PATTERNS.host_address,
      url: VALIDATION_PATTERNS.url,
      input_number: NUMBER_ONLY_PATTERN,
    };

    const activePage = settingsPages.find((p) => String(p.uid) === activeTab);
    if (!activePage) {
      setInvalidFieldLabels(Array.from(invalidFields));
      return false;
    }
    let isValid = true;

    activePage.forms?.forEach((form) => {
      form.fieldList
        .filter(
          (field: any) =>
            field.fieldType !== "testConnectionBtn" &&
            field.fieldType !== "title" &&
            field.givenName !== "title",
        )
        .forEach((field: any) => {
          const isNumberField =
            field.fieldType === "input_number" ||
            field.fieldType === "number" ||
            field.field_subtype === "input_number" ||
            field.field_subtype === "number";

          if (field.fieldType === "region_country") {
            if (!selectedRegion || !selectedCountry) {
              isValid = false;
              markInvalidField(field, "Region & Country");
            }
            return;
          }

          if (field.fieldType === "proxy" || field.givenName === "proxy") {
            return;
          }

          if (field.fieldType === "dropdown") {
            const value = dropdownValues[field.givenName];
            if (!value || value === "select") {
              isValid = false;
              markInvalidField(field);
            }
            return;
          }

          if (field.fieldType === "radio") {
            if (!dropdownValues[field.givenName]) {
              isValid = false;
              markInvalidField(field);
            }
            return;
          }

          if (field.fieldType === "multi_select_dropdown") {
            const value = dropdownValues[field.givenName];
            if (!Array.isArray(value) || value.length === 0) {
              isValid = false;
              markInvalidField(field);
            }
            return;
          }

          if (field.fieldType === "checkbox") {
            const elements = fieldRefs.current[field.givenName];
            const hasChecked =
              Array.isArray(elements) && elements.some((el: any) => el.checked);
            if (!hasChecked) {
              isValid = false;
              markInvalidField(field);
            }
            return;
          }

          if (field.fieldType === "input_date") {
            const refItem = fieldRefs.current[field.givenName];
            const hiddenValue = Array.isArray(refItem)
              ? refItem[0]?.value?.trim() || ""
              : refItem?.value?.trim() || "";
            const selectedDate = (dateValues[field.givenName] || "").trim();
            if (!(selectedDate || hiddenValue)) {
              isValid = false;
              markInvalidField(field);
            }
            return;
          }

          const inputEl = fieldRefs.current[field.givenName];
          if (!inputEl) {
            isValid = false;
            markInvalidField(field);
            return;
          }

          let value = "";
          if (Array.isArray(inputEl)) {
            value = inputEl[0]?.value?.trim() || "";
          } else {
            value = inputEl.value?.trim() || "";
          }

          if (!value) {
            isValid = false;
            markInvalidField(field);
            return;
          }

          if (isNumberField && !NUMBER_ONLY_PATTERN.test(value)) {
            isValid = false;
            markInvalidField(field);
            return;
          }

          const validator =
            validators[field.fieldType as keyof typeof validators];
          if (validator && !validator.test(value)) {
            isValid = false;
            markInvalidField(field);
          }
        });
    });

    setInvalidFieldLabels(Array.from(invalidFields));
    return isValid;
  };

  // Form validation
  // React.useEffect(() => {
  //   const checkValidity = () => {
  //     setIsFormValid(validateForm());
  //   };

  //   // Listen for input changes
  //   const inputs = Object.values(fieldRefs.current);
  //   inputs.forEach((input: any) =>
  //     input?.addEventListener("input", checkValidity),
  //   );

  //   // Also check title
  //   titleRef.current?.addEventListener("input", checkValidity);

  //   // Cleanup
  //   return () => {
  //     inputs.forEach((input: any) =>
  //       input?.removeEventListener("input", checkValidity),
  //     );
  //     titleRef.current?.removeEventListener("input", checkValidity);
  //   };
  // }, [fieldRefs.current, titleRef.current]);

  React.useEffect(() => {
    const checkValidity = () => {
      setIsFormValid(validateForm());
    };

    const inputs = Object.values(fieldRefs.current || {});

    inputs.forEach((item: any) => {
      const elements = Array.isArray(item) ? item : [item];
      elements.forEach((input) => {
        if (!(input instanceof HTMLElement)) return;
        input.addEventListener("input", checkValidity);
        input.addEventListener("change", checkValidity);
      });
    });

    titleRef.current?.addEventListener("input", checkValidity);

    checkValidity();

    return () => {
      inputs.forEach((item: any) => {
        const elements = Array.isArray(item) ? item : [item];
        elements.forEach((input) => {
          if (!(input instanceof HTMLElement)) return;
          input.removeEventListener("input", checkValidity);
          input.removeEventListener("change", checkValidity);
        });
      });

      titleRef.current?.removeEventListener("input", checkValidity);
    };
  }, [
    pages,
    activeTab,
    isEditing,
    editingIndex,
    selectedRegion,
    selectedCountry,
    dateValues,
  ]);

  // Handle test connection
  const handleTestConnection = async () => {
    const liveFormData: Record<string, any> = {
      title: titleRef.current?.value?.trim() || "",
    };

    const activePage = settingsPages.find((p) => String(p.uid) === activeTab);
    if (activePage) {
      activePage.forms?.forEach((form) => {
        form.fieldList
          .filter(
            (field: any) =>
              field.fieldType !== "testConnectionBtn" &&
              field.fieldType !== "title" &&
              field.givenName !== "title",
          )
          .forEach((field: any) => {
            const key = field.givenName;
            if (!key) return;

            if (field.fieldType === "proxy" || field.givenName === "proxy") {
              liveFormData[key] = switchValues[key] ?? false;
              if (isMeshV2) {
                liveFormData[`${key}_epochtime`] =
                  proxyServerSelections[key] || "";
              }
              return;
            }

            if (field.fieldType === "region_country") {
              liveFormData[key] = JSON.stringify({
                region: selectedRegion,
                country: selectedCountry,
              });
              return;
            }

            if (
              field.fieldType === "dropdown" ||
              field.fieldType === "radio" ||
              field.fieldType === "multi_select_dropdown"
            ) {
              liveFormData[key] = dropdownValues[key] ?? "";
              return;
            }

            if (field.fieldType === "input_date") {
              const ref = fieldRefs.current[key];
              const refValue = Array.isArray(ref)
                ? ref[ref.length - 1]?.value || ""
                : ref?.value || "";
              liveFormData[key] = dateValues[key] || refValue;
              return;
            }

            if (field.fieldType === "checkbox") {
              const refs = fieldRefs.current[key];
              if (Array.isArray(refs)) {
                liveFormData[key] = refs
                  .filter((el: any) => el?.checked)
                  .map((el: any) => el.value);
              } else {
                liveFormData[key] = [];
              }
              return;
            }

            const ref = fieldRefs.current[key];
            if (Array.isArray(ref)) {
              liveFormData[key] = ref[0]?.value || "";
            } else {
              liveFormData[key] = ref?.value || "";
            }
          });
      });
    }

    setData(liveFormData);
    setLoading(true);

    // Check if it's a workflow-based test connection (mesh 2.0)
    const isWorkflowBased =
      !!testConnectionRequest.workflow_id &&
      testConnectionRequest.workflow_id.startsWith("workflow_");

    if (isWorkflowBased) {
      // Mesh 2.0: Execute workflow with replaced config values

      if (!workflowData?.data) {
        console.error("Workflow data not available:", workflowData);
        toast({
          title: "Error",
          description: "Workflow data not loaded. Please try again.",
          variant: "destructive",
          duration: 3000,
        });
        setLoading(false);
        return;
      }

      const workflow = workflowData.data;

      // Check if we're testing a new (unsaved) configuration
      // If not editing OR editingIndex is null, it's a new config
      const isNewConfig = !isEditing || editingIndex === null;
      const effectiveConfigUid = isNewConfig ? "" : config?.uid || "";
      const effectiveTabId = isNewConfig ? "" : config?.tabId || "";

      // Clone workflow nodes to avoid modifying original
      const updatedNodes = workflow.nodes.map((node: any) => {
        // Find config nodes and update their configData with form data
        if (node.type === "config" && node.sub_type === "config_node") {
          if (!node.parameters) return node;

          const updatedNode = {
            ...node,
            parameters: {
              ...node.parameters,
              pluginId: pluginId ?? node.parameters.pluginId,
              configType: "temp", // Keep as temp for UiPreview
              configData: {
                ...node.parameters.configData,
                ...liveFormData, // Replace with latest form data
                pluginId: pluginId ?? node.parameters.configData?.pluginId,
                uid: effectiveConfigUid,
                tabId: effectiveTabId,
                type: "temp", // Keep as temp for UiPreview
                configType: "temp", // Keep as temp for UiPreview
              },
            },
          };

          updatedNode.parameters.Uid = effectiveConfigUid;
          updatedNode.parameters.TabId = effectiveTabId;

          return updatedNode;
        }

        // Update downstream nodes that reference config
        // Remove or update config references in their parameters
        if (node.parameters?.config) {
          const updatedNode = {
            ...node,
            parameters: {
              ...node.parameters,
              config: isNewConfig
                ? {
                    tabId: effectiveTabId,
                    uid: effectiveConfigUid,
                    configType: "temp",
                  }
                : {
                    tabId:
                      effectiveTabId ||
                      config?.tabId ||
                      node.parameters.config.tabId,
                    uid:
                      effectiveConfigUid ||
                      config?.uid ||
                      node.parameters.config.uid,
                    configType: "temp",
                  },
              // Also update the data object if it exists
              data: {
                ...node.parameters.data,
                ...liveFormData, // Use latest form data
                type: "temp",
              },
            },
          };
          return updatedNode;
        }

        return node;
      });

      // Prepare workflow execution payload
      const workflowPayload = {
        workflowData: {
          uid: workflow.uid,
          title: workflow.title || workflow.workflow_name || "Test Connection",
          workflow_id: testConnectionRequest.workflow_id,
          plugin_id: pluginId ?? "",
          nodes: updatedNodes,
          connections: workflow.connections,
          workflow_name:
            workflow.title || workflow.workflow_name || "Test Connection",
          is_test_connection: true,
          type_workflow: workflow.type_workflow || "settings",
          cron_run_time: workflow.cron_run_time || null,
          dependency_workflow: workflow.dependency_workflow || [],
          is_active: workflow.is_active ?? true,
          description: workflow.description || null,
          version: workflow.version || "1.0",
          threatlookup_data: workflow.threatlookup_data || {},
          threat_lookup_search_types: workflow.threat_lookup_search_types || [],
        },
        plugin_id: pluginId ?? "",
        configuration_Id: effectiveConfigUid,
      };

      // Execute workflow
      executeWorkflow(workflowPayload)
        .then((response) => {
          // Check if workflow execution actually succeeded
          const isSuccess = response?.success && response?.status === "success";
          const workflowResult = response?.results?.[0];

          if (!isSuccess || workflowResult?.status !== "success") {
            // Execution failed - extract error message
            let errorMessage = "";

            // Primary source: specific node-level errors
            if (workflowResult?.node_results) {
              const failedNode = workflowResult.node_results.find(
                (node: any) => node.status !== "success" && node.error,
              );
              if (failedNode) {
                const nodeError = failedNode.error;
                let detail = "";
                if (Array.isArray(nodeError)) {
                  detail = nodeError
                    .map((e: any) => e.message || e.error || JSON.stringify(e))
                    .join(", ");
                } else if (typeof nodeError === "string") {
                  detail = nodeError;
                } else {
                  detail = JSON.stringify(nodeError);
                }
                errorMessage = `Node '${failedNode.title}' failed: ${detail}`;
              }
            }

            // Secondary sources if no node error found
            if (!errorMessage) {
              if (workflowResult?.error) {
                errorMessage =
                  typeof workflowResult.error === "string"
                    ? workflowResult.error
                    : JSON.stringify(workflowResult.error);
              } else if (response?.error) {
                errorMessage = response.error;
              } else if (response?.message) {
                errorMessage = response.message;
              } else {
                errorMessage = "Test Connection Failed.";
              }
            }

            toast({
              title: "Test Connection Failed",
              description: errorMessage,
              variant: "destructive",
              duration: 3000,
            });
            setLoading(false);
            return;
          }

          // Success case
          toast({
            title: "Test Connection Successful",
            description: `Test Connection Successful with status code 200`,
            variant: "success",
            duration: 3000,
          });
          setLoading(false);
        })
        .catch((err: any) => {
          console.error("Workflow execution error:", err);
          let errorMessage = "Could not connect.";

          try {
            if (err?.response?.data) {
              const errorData = err.response.data;

              // Handle different error response structures
              if (errorData.error) {
                errorMessage = errorData.error;
              } else if (errorData.message) {
                errorMessage = errorData.message;
              } else if (errorData.details) {
                // Handle validation errors
                const details = Array.isArray(errorData.details)
                  ? errorData.details.map((d: any) => d.msg).join(", ")
                  : JSON.stringify(errorData.details);
                errorMessage = `Validation error: ${details}`;
              }
            } else if (err?.message) {
              errorMessage = err.message;
            }
          } catch (parseError) {
            errorMessage =
              "An unexpected error occurred during test connection.";
          }

          toast({
            title: "Test Connection Failed",
            description: errorMessage,
            variant: "destructive",
            duration: 3000,
          });
          setLoading(false);
        });
    } else {
      const payload = {
        requestId: requestData?.requestId ?? "",
        plugin_id: pluginId ?? "",
        url: requestData?.url ?? "",
        method: requestData?.method ?? "GET",
        params: requestData?.params ?? [],
        headers: (requestData?.headers ?? [])?.map((h: any) => ({
          key: h.key ?? "",
          value: h.value ?? "",
        })),
        auth: requestData?.auth ?? {},
        body: requestData?.body ?? null,
        body_type: requestData?.body_type ?? "none",
        raw_type: requestData?.raw_type ?? null,
        isLoop: requestData?.isLoop ?? false,
        loopSourcePath: requestData?.loopSourcePath ?? "",
        loopSourceRequest: requestData?.loopSourceRequest ?? null,
        loopSourceRequestBundle: requestData?.loopSourceRequestBundle ?? null,
        checkpoint: requestData?.checkpoint ?? {},
        flag: 1,
        ssl_verify: requestData?.ssl_verify ?? true,
        configuration_Id: requestData?.configuration_Id ?? "",
        mappingId: requestData?.mappingId ?? "",
        pagination: requestData?.pagination ?? { type: "none" },
        filteration: requestData?.filteration ?? {
          type: "none",
          default_cron_start_time: "none",
          start_key: "",
          end_key: "",
          custom_date_format: "",
        },
        config: config ?? {},
        data: data ?? {},
      };

      testConnectionMutation.mutate(payload);
    }
  };

  const getCustomSelectFormValues = (items: any[]) => {
    return items.map((item: any) => {
      return {
        ...item,
        label: item.displayName,
        value: item.value,
      };
    });
  };

  return (
    <>
      <style>{HELP_DOC_STYLES}</style>
      <Dialog open={open} onOpenChange={handleClose} modal={false}>
        <div
          className={
            "fixed inset-0 z-50 " +
            (open ? "block" : "hidden") +
            (String(meshVersion) === "2.0" ? " top-[-14px]" : "")
          }
        >
          <div className="relative w-full h-full">
            <div className="absolute inset-0 bg-modalBackground" />
          </div>
        </div>
        {pendingDelete && (
          <Confirmation
            text="Are you sure you want to delete this configuration?"
            confirmAction={onDeleteConfirm}
            rejectAction={onDeleteCancel}
            confirmText="Yes, delete it!"
            rejectText="Cancel"
            show={!!pendingDelete}
            closeDialog={onDeleteCancel}
          />
        )}

        <PageLoader
          show={
            isConfigLoading ||
            loading ||
            isLogicLoading ||
            isWorkflowLoading ||
            createOrUpdateConfigMutation.isLoading
          }
        />
        {saveChanges && (
          <Confirmation
            text="You have unsaved changes. Do you want to discard them?"
            confirmAction={() => {
              setSaveChanges(false);
              setIsEditing(false);
              setEditingIndex(null);
              clearForm();
              setOpenPreview(false);
            }}
            rejectAction={() => setSaveChanges(false)}
            confirmText="Yes, discard"
            rejectText="No, keep editing"
            show={saveChanges}
            closeDialog={() => setSaveChanges(false)}
          />
        )}

        <DialogContent
          className="sm:max-w-[80vw] max-h-[90vh] min-h-[80vh] !flex !flex-col overflow-hidden"
          onInteractOutside={(event) => {
            event.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle>Settings - {resolvedPluginName} </DialogTitle>
          </DialogHeader>

          <Tabs
            value={activeTab ?? ""}
            onValueChange={(value) => {
              setActiveTab(value);
            }}
            className="flex-1 flex flex-col h-full overflow-auto border rounded-md p-3 min-h-[80vh]"
          >
            <TabsList className="flex justify-start w-content border-b h-12">
              {settingsPages?.map((page) => (
                <TabsTrigger
                  key={page.uid}
                  value={String(page.uid)}
                  className="min-w-[100px] data-[state=active]:bg-primary data-[state=active]:text-background rounded-md h-9"
                >
                  {page.pageTitle || "Settings"}
                </TabsTrigger>
              ))}

              {helpPages?.map((page) => (
                <TabsTrigger
                  key={page.uid}
                  value={String(page.uid)}
                  className="min-w-[100px] data-[state=active]:bg-primary data-[state=active]:text-background rounded-md h-9"
                >
                  {page.pageTitle || "Help"}
                </TabsTrigger>
              ))}
            </TabsList>

            {settingsPages?.map((page) => {
              const pageUid = String(page.uid);
              const settingsList =
                configurationData?.find(
                  (entry) => String(entry.pageUid) === pageUid,
                )?.data ?? [];

              return (
                <TabsContent
                  key={pageUid}
                  value={pageUid}
                  className="flex-1 overflow-y-auto min-h-0 mt-3 border p-3 rounded-md space-y-4"
                >
                  <div>
                    <div className="mb-4 text-sm m-2">
                      <label className="block font-medium text-sm mb-1">
                        Title
                      </label>
                      <input
                        ref={titleRef}
                        type="text"
                        placeholder="Enter Title"
                        className="border rounded-md p-2 w-full bg-transparent border-secondary focus:ring-secondary focus:ring-1"
                        required
                      />
                    </div>

                    {page.forms?.map((form, fIdx) => (
                      <div key={fIdx} className="text-sm m-2">
                        {form.fieldList
                          .filter(
                            (field: any) =>
                              field.fieldType !== "testConnectionBtn" &&
                              field.fieldType !== "title",
                          )
                          ?.map((field: any, fieldIdx: number) => (
                            <div key={fieldIdx} className="mb-4">
                              <div className="flex justify-between">
                                <label className="flex text-sm mb-1">
                                  {field.fieldTitle}{" "}
                                  {field.fieldType === "proxy" ||
                                  field.givenName === "proxy" ? (
                                    <CheckCheck
                                      size={20}
                                      className={`${switchValues[field.givenName] ? "text-networkGreen" : "text-lightGray"}  ml-2`}
                                    />
                                  ) : (
                                    ""
                                  )}
                                </label>
                                {field.infoIcon &&
                                  field.infoIcon.trim() !== "" && (
                                    <InfoTooltip
                                      text={
                                        isEditing &&
                                        editingIndex !== null &&
                                        field.fieldType === "input_password"
                                          ? `${field.fieldTitle} is prefilled as encrypted form, if any changes needed in the ${field.fieldTitle}, please replace it with the new value.`
                                          : field.infoIcon
                                      }
                                    />
                                  )}
                              </div>
                              {field.fieldType === "dropdown" ? (
                                <Select
                                  value={(() => {
                                    const selectedRaw =
                                      field.givenName in dropdownValues
                                        ? dropdownValues[field.givenName]
                                        : field.default;
                                    const selected =
                                      selectedRaw == null
                                        ? ""
                                        : String(selectedRaw);
                                    const hasSelectedOption =
                                      field?.values?.some(
                                        (option: any) =>
                                          String(option.value) === selected,
                                      ) || false;

                                    return selected &&
                                      selected !== "select" &&
                                      hasSelectedOption
                                      ? selected
                                      : undefined;
                                  })()}
                                  onValueChange={(value) => {
                                    setDropdownValues((prev) => ({
                                      ...prev,
                                      [field.givenName]: value,
                                    }));
                                  }}
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue
                                      placeholder={
                                        field.placeholder || "Select an option"
                                      }
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectGroup>
                                      <SelectItem value="select">
                                        Select an option
                                      </SelectItem>
                                      {field?.values?.map(
                                        (option: any, oIdx: any) => (
                                          <SelectItem
                                            key={oIdx}
                                            value={option.value}
                                          >
                                            {option.displayName}
                                          </SelectItem>
                                        ),
                                      )}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              ) : field.fieldType === "radio" ? (
                                <RadioGroup
                                  name={field.givenName}
                                  value={
                                    dropdownValues[field.givenName] ??
                                    field.default ??
                                    ""
                                  }
                                  className="flex items-center gap-4"
                                  onValueChange={(value) => {
                                    setDropdownValues((prev) => ({
                                      ...prev,
                                      [field.givenName]: value,
                                    }));
                                    triggerValidation();
                                  }}
                                >
                                  {field?.values?.map(
                                    (option: any, oIdx: any) => (
                                      <div
                                        key={oIdx}
                                        className="flex items-center gap-1"
                                      >
                                        <RadioGroupItem
                                          value={option.value}
                                          id={`${field.givenName}-${oIdx}`}
                                        />
                                        <Label
                                          htmlFor={`${field.givenName}-${oIdx}`}
                                        >
                                          {option.displayName}
                                        </Label>
                                      </div>
                                    ),
                                  )}
                                </RadioGroup>
                              ) : field.fieldType === "checkbox" ? (
                                <div className="flex flex-col gap-2">
                                  {field?.values?.map(
                                    (option: any, oIdx: number) => (
                                      <label
                                        key={oIdx}
                                        className="flex items-center gap-2"
                                      >
                                        <input
                                          type="checkbox"
                                          name={field.givenName}
                                          value={option.value}
                                          defaultChecked={
                                            Array.isArray(field.default)
                                              ? field.default.includes(
                                                  option.value,
                                                )
                                              : field.default === option.value
                                          }
                                          ref={(el) => {
                                            if (!el) return;
                                            const name = field.givenName;
                                            const existing =
                                              fieldRefs.current[name];
                                            if (!existing) {
                                              fieldRefs.current[name] = [el];
                                            } else if (
                                              Array.isArray(existing)
                                            ) {
                                              if (!existing.includes(el)) {
                                                existing.push(el);
                                              }
                                            } else {
                                              if (existing !== el) {
                                                fieldRefs.current[name] = [
                                                  existing,
                                                  el,
                                                ];
                                              }
                                            }
                                          }}
                                        />
                                        {option.displayName}
                                      </label>
                                    ),
                                  )}
                                </div>
                              ) : field.fieldType ===
                                "multi_select_dropdown" ? (
                                <div className="w-full min-w-0 overflow-hidden rounded-md border">
                                  <CustomSelect
                                    className="w-full min-w-0 truncate"
                                    data={
                                      getCustomSelectFormValues(field.values) ||
                                      []
                                    }
                                    defaultValue={
                                      Array.isArray(
                                        dropdownValues[field.givenName],
                                      )
                                        ? dropdownValues[field.givenName]
                                        : dropdownValues[field.givenName]
                                          ? [dropdownValues[field.givenName]]
                                          : []
                                    }
                                    placeholder="Select"
                                    onSelect={(value) =>
                                      setDropdownValues((prev) => ({
                                        ...prev,
                                        [field.givenName]: value as any,
                                      }))
                                    }
                                    multiSelect={true}
                                  />
                                </div>
                              ) : field.fieldType === "region_country" ? (
                                <div className="flex items-center gap-4 border rounded-lg p-1 w-full bg-transparent border-secondary">
                                  <CustomSelect
                                    key={`region-${field.givenName}-${isEditing}-${editingIndex}`}
                                    data={regionOptions}
                                    placeholder="Select a Region"
                                    defaultValue={
                                      selectedRegion ? [selectedRegion] : []
                                    }
                                    multiSelect={false}
                                    onSelect={(value) => {
                                      setSelectedRegion(value);
                                      setSelectedCountry("");
                                      triggerValidation();
                                    }}
                                    className="w-full"
                                  />

                                  <CustomSelect
                                    key={`country-${field.givenName}-${selectedRegion}-${isEditing}-${editingIndex}`}
                                    data={countryOptions}
                                    placeholder={`Select a Country ${selectedRegion ? `in ${selectedRegion}` : ""}`}
                                    defaultValue={
                                      selectedCountry ? [selectedCountry] : []
                                    }
                                    multiSelect={false}
                                    onSelect={(value) => {
                                      setSelectedCountry(value);
                                      triggerValidation();
                                    }}
                                    disabled={!selectedRegion}
                                    className={`w-full ${!selectedRegion ? "opacity-50 cursor-not-allowed" : ""}`}
                                  />
                                </div>
                              ) : field.fieldType === "proxy" ||
                                field.givenName === "proxy" ? (
                                <div className="py-1 flex items-center gap-5 h-8">
                                  <Switch
                                    checked={
                                      switchValues[field.givenName] ?? false
                                    }
                                    onCheckedChange={(checked) => {
                                      setSwitchValues((prev) => ({
                                        ...prev,
                                        [field.givenName]: checked,
                                      }));
                                      if (!checked) {
                                        setProxyServerSelections((prev) => ({
                                          ...prev,
                                          [field.givenName]: "",
                                        }));
                                      }
                                      setData((prev: any) => ({
                                        ...prev,
                                        [field.givenName]: checked,
                                        ...(isMeshV2 && !checked
                                          ? {
                                              [`${field.givenName}_epochtime`]:
                                                "",
                                            }
                                          : {}),
                                      }));
                                      triggerValidation();
                                    }}
                                  />
                                </div>
                              ) : field.fieldType === "input_date" ? (
                                <div className="w-full">
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        className="w-full justify-between border-secondary bg-transparent font-normal text-left"
                                      >
                                        <span className="truncate">
                                          {dateValues[field.givenName] ||
                                            (typeof field.default === "string"
                                              ? field.default
                                              : "") ||
                                            field.placeholder ||
                                            "Pick a date"}
                                        </span>
                                        <CalendarIcon className="h-4 w-4 opacity-60" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                      className="w-auto p-0"
                                      align="start"
                                    >
                                      <Calendar
                                        mode="single"
                                        selected={
                                          dateValues[field.givenName]
                                            ? new Date(
                                                dateValues[field.givenName],
                                              )
                                            : typeof field.default ===
                                                  "string" && field.default
                                              ? new Date(field.default)
                                              : undefined
                                        }
                                        onSelect={(date) => {
                                          const formattedDate = date
                                            ? format(date, "yyyy-MM-dd")
                                            : "";
                                          setDateFieldValue(
                                            field.givenName,
                                            formattedDate,
                                          );
                                        }}
                                        initialFocus
                                      />
                                    </PopoverContent>
                                  </Popover>
                                  <input
                                    type="hidden"
                                    value={
                                      dateValues[field.givenName] ||
                                      (typeof field.default === "string"
                                        ? field.default
                                        : "")
                                    }
                                    ref={(el: any) => {
                                      if (el) {
                                        fieldRefs.current[field.givenName] = el;
                                      }
                                    }}
                                    onChange={() => triggerValidation()}
                                    readOnly
                                  />
                                </div>
                              ) : (
                                <>
                                  <input
                                    type={
                                      field.fieldType == "password" ||
                                      field.fieldType == "input_password" ||
                                      field.field_subtype == "input_password"
                                        ? "password"
                                        : field.fieldType === "port"
                                          ? "number"
                                          : "text"
                                    }
                                    placeholder={
                                      field.placeholder ||
                                      field.fieldTitle ||
                                      ""
                                    }
                                    defaultValue={field.default || ""}
                                    className="border rounded-md p-2 w-full bg-transparent border-secondary focus:ring-secondary focus:ring-1"
                                    ref={(el: any) => {
                                      if (!el) return;

                                      if (!fieldRefs.current[field.givenName]) {
                                        fieldRefs.current[field.givenName] = [];
                                      }
                                      if (
                                        !(
                                          fieldRefs.current[
                                            field.givenName
                                          ] as any[]
                                        ).includes(el)
                                      ) {
                                        (
                                          fieldRefs.current[
                                            field.givenName
                                          ] as any[]
                                        ).push(el);
                                      }
                                    }}
                                    onChange={(e) => {
                                      const isNumberField =
                                        field.fieldType === "input_number" ||
                                        field.fieldType === "number" ||
                                        field.field_subtype ===
                                          "input_number" ||
                                        field.field_subtype === "number";
                                      const isPortField =
                                        field.fieldType === "port";
                                      const rawValue = e.target.value;
                                      const value =
                                        isNumberField || isPortField
                                          ? rawValue.replace(/\D+/g, "")
                                          : rawValue;

                                      if (isNumberField && value !== rawValue) {
                                        e.target.value = value;
                                      }

                                      const isValid =
                                        field.fieldType === "ip"
                                          ? VALIDATION_PATTERNS.ip.test(value)
                                          : field.fieldType === "domain"
                                            ? VALIDATION_PATTERNS.domain.test(
                                                value,
                                              )
                                            : field.fieldType === "mac"
                                              ? VALIDATION_PATTERNS.mac.test(
                                                  value,
                                                )
                                              : field.fieldType === "port"
                                                ? VALIDATION_PATTERNS.port.test(
                                                    value,
                                                  )
                                                : isNumberField
                                                  ? NUMBER_ONLY_PATTERN.test(
                                                      value,
                                                    )
                                                  : field.fieldType ===
                                                      "host_address"
                                                    ? VALIDATION_PATTERNS.host_address.test(
                                                        value,
                                                      )
                                                    : field.fieldType === "url"
                                                      ? VALIDATION_PATTERNS.url.test(
                                                          value,
                                                        )
                                                      : true;

                                      const fieldName = field.givenName;
                                      const fieldLabel =
                                        String(field.fieldTitle || "").trim() ||
                                        String(field.givenName || "").trim() ||
                                        "Field";
                                      const fieldTypeDisplayName =
                                        getFieldTypeDisplayName(field);
                                      if (
                                        value &&
                                        !isValid &&
                                        (field.fieldType === "ip" ||
                                          field.fieldType === "domain" ||
                                          field.fieldType === "mac" ||
                                          field.fieldType === "port" ||
                                          isNumberField ||
                                          field.fieldType === "host_address" ||
                                          field.fieldType === "url")
                                      ) {
                                        e.target.setCustomValidity(
                                          "Invalid format",
                                        );
                                        e.target.classList.add(
                                          "border-primary",
                                        );
                                        e.target.classList.remove(
                                          "border-secondary",
                                        );
                                        fieldValidationState.current[
                                          fieldName
                                        ] = false;
                                        setLiveInvalidFieldMessages((prev) => ({
                                          ...prev,
                                          [fieldName]: `${fieldLabel} is invalid. Please enter a valid ${fieldTypeDisplayName}.`,
                                        }));
                                      } else {
                                        e.target.setCustomValidity("");
                                        e.target.classList.remove(
                                          "border-primary",
                                        );
                                        e.target.classList.add(
                                          "border-secondary",
                                        );
                                        fieldValidationState.current[
                                          fieldName
                                        ] = true;
                                        setLiveInvalidFieldMessages((prev) => {
                                          if (!prev[fieldName]) return prev;
                                          const next = { ...prev };
                                          delete next[fieldName];
                                          return next;
                                        });
                                      }
                                      triggerValidation();
                                    }}
                                    onKeyDown={(e) => {
                                      if (
                                        field.fieldType === "port" &&
                                        ["e", "E", "+", "-", "."].includes(
                                          e.key,
                                        )
                                      ) {
                                        e.preventDefault();
                                      }
                                    }}
                                    {...(field.fieldType === "port" && {
                                      min: 1,
                                      max: 65535,
                                    })}
                                    {...((field.fieldType === "input_number" ||
                                      field.fieldType === "number" ||
                                      field.field_subtype === "input_number" ||
                                      field.field_subtype === "number") && {
                                      inputMode: "numeric",
                                      pattern: "[0-9]*",
                                    })}
                                  />
                                </>
                              )}
                            </div>
                          ))}
                      </div>
                    ))}

                    <div className="flex justify-start gap-2 mt-6 m-2">
                      <Button
                        className="bg-primary text-background "
                        onClick={() => {
                          clearForm();
                          setIsEditing(false);
                          setEditingIndex(null);
                        }}
                        disabled={areAllFieldsEmpty()}
                      >
                        Clear
                      </Button>
                      {/* Only show Test Connection if there's a test connection field AND all fields are filled AND form is valid */}
                      {page.forms?.some((form) =>
                        form.fieldList.some(
                          (field: any) =>
                            field.fieldType === "testConnectionBtn",
                        ),
                      ) &&
                        areAllFieldsFilled() &&
                        isFormValid && (
                          <Button
                            className="bg-primary text-background "
                            onClick={handleTestConnection}
                          >
                            Test Connection
                          </Button>
                        )}
                      <Button
                        type="button"
                        onClick={handleSettingsSubmit}
                        disabled={!isFormValid}
                      >
                        Submit
                      </Button>
                      {(Object.keys(liveInvalidFieldMessages).length > 0 ||
                        (hasSubmitValidationError &&
                          !isFormValid &&
                          invalidFieldLabels.length > 0)) && (
                        <div
                          className="inline-flex items-center gap-1 text-xs text-primaryAmber px-2 py-1 bg-primary/5"
                          title={
                            Object.keys(liveInvalidFieldMessages).length > 0
                              ? Object.values(liveInvalidFieldMessages).join(
                                  " ",
                                )
                              : `Invalid fields: ${invalidFieldLabels.join(", ")}`
                          }
                        >
                          <TriangleAlert size={18} className="mr-1" />
                          <span>
                            {Object.keys(liveInvalidFieldMessages).length > 0
                              ? Object.values(liveInvalidFieldMessages)[0]
                              : `Invalid fields (${invalidFieldLabels.length})`}
                          </span>
                        </div>
                      )}
                    </div>

                    {settingsList.length === 0 && (
                      <FallBackUi
                        title="No Settings saved yet !"
                        description="Create a new configuration or test connection."
                      />
                    )}

                    {settingsList.length > 0 && (
                      <div className="overflow-x-auto mt-10 mx-2 border rounded-lg">
                        <table className="min-w-full border border-border shadow-lg rounded-lg overflow-hidden text-sm">
                          <thead>
                            <tr>
                              {/* Generate headers from current form structure, not just saved data */}
                              {[
                                "title",
                                ...(page.forms?.flatMap((form: any) =>
                                  form.fieldList
                                    .filter((field: any) => {
                                      // Exclude testConnectionBtn, title fields, hiddenKeys, and encrypted fields
                                      const isEncrypted =
                                        field.fieldType === "password" ||
                                        field.fieldType === "input_password" ||
                                        field.field_subtype ===
                                          "input_password" ||
                                        field.isDecript;
                                      return (
                                        field.fieldType !==
                                          "testConnectionBtn" &&
                                        field.fieldType !== "title" &&
                                        field.givenName !== "title" &&
                                        !hiddenKeys.includes(field.givenName) &&
                                        !isEncrypted
                                      );
                                    })
                                    .flatMap((field: any) => {
                                      if (
                                        field.fieldType === "region_country"
                                      ) {
                                        return [
                                          `${field.givenName}_region`,
                                          `${field.givenName}_country`,
                                        ];
                                      }
                                      return [field.givenName];
                                    }),
                                ) || []),
                              ]
                                .filter(
                                  (key) => key && !hiddenKeys.includes(key),
                                )
                                .map((key) => (
                                  <th
                                    key={key}
                                    className="px-6 py-3 border-b border-border font-semibold tracking-wide text-sm text-foreground text-left"
                                  >
                                    {key.endsWith("_region")
                                      ? "Region"
                                      : key.endsWith("_country")
                                        ? "Country"
                                        : key}
                                  </th>
                                ))}
                              <th className="px-6 py-3 border-b border-border font-semibold tracking-wide text-sm text-foreground">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {settingsList?.map(
                              (setting: any, index: number) => {
                                // Get all field names from current form structure, excluding encrypted fields
                                const allFieldNames = [
                                  "title",
                                  ...(page.forms?.flatMap((form: any) =>
                                    form.fieldList
                                      .filter((field: any) => {
                                        const isEncrypted =
                                          field.fieldType === "password" ||
                                          field.fieldType ===
                                            "input_password" ||
                                          field.field_subtype ===
                                            "input_password" ||
                                          field.isDecript;
                                        return (
                                          field.fieldType !==
                                            "testConnectionBtn" &&
                                          field.fieldType !== "title" &&
                                          field.givenName !== "title" &&
                                          !hiddenKeys.includes(
                                            field.givenName,
                                          ) &&
                                          !isEncrypted
                                        );
                                      })
                                      .flatMap((field: any) => {
                                        if (
                                          field.fieldType === "region_country"
                                        ) {
                                          return [
                                            `${field.givenName}_region`,
                                            `${field.givenName}_country`,
                                          ];
                                        }
                                        return [field.givenName];
                                      }),
                                  ) || []),
                                ].filter(
                                  (key) => key && !hiddenKeys.includes(key),
                                );

                                return (
                                  <tr
                                    key={index}
                                    className={`border-b border-border transition-colors duration-150 ${
                                      index % 2 === 0
                                        ? "bg-muted"
                                        : "bg-background"
                                    } hover:bg-muted-foreground`}
                                  >
                                    {allFieldNames?.map((key, vIdx) => {
                                      const fieldDef = page.forms
                                        ?.flatMap((form: any) => form.fieldList)
                                        .find(
                                          (field: any) =>
                                            field.givenName === key,
                                        );
                                      const isEncrypted =
                                        fieldDef?.fieldType === "password" ||
                                        fieldDef?.fieldType ===
                                          "input_password" ||
                                        fieldDef?.field_subtype ===
                                          "input_password" ||
                                        fieldDef?.isDecript;
                                      if (!isEncrypted) {
                                        let displayVal = setting[key];

                                        // Handle region_country — stored as JSON string, display _region/_country suffix
                                        if (
                                          key.endsWith("_region") ||
                                          key.endsWith("_country")
                                        ) {
                                          const baseKey = key
                                            .replace(/_region$/, "")
                                            .replace(/_country$/, "");
                                          const raw = setting[baseKey];
                                          try {
                                            const parsed =
                                              typeof raw === "string"
                                                ? JSON.parse(raw)
                                                : raw;
                                            displayVal = key.endsWith("_region")
                                              ? parsed?.region
                                              : parsed?.country;
                                          } catch {
                                            displayVal = "";
                                          }
                                        }

                                        if (
                                          (displayVal === undefined ||
                                            displayVal === "") &&
                                          fieldDef?.default
                                        ) {
                                          displayVal = fieldDef.default;
                                        }

                                        // Normalize multi_select_dropdown: stored as "a,b,c" string → split to array
                                        if (
                                          fieldDef?.fieldType ===
                                            "multi_select_dropdown" &&
                                          typeof displayVal === "string" &&
                                          displayVal
                                        ) {
                                          displayVal = displayVal.split(",");
                                        }

                                        const cellText = Array.isArray(
                                          displayVal,
                                        )
                                          ? displayVal
                                              .map((item: any) =>
                                                typeof item === "object"
                                                  ? item.label || item.value
                                                  : String(item),
                                              )
                                              .join(", ")
                                          : String(displayVal ?? "");

                                        return (
                                          <td
                                            key={vIdx}
                                            className="px-6 py-3 text-foreground max-w-[200px] break-words"
                                          >
                                            {key === "proxy"
                                              ? toBoolean(displayVal)
                                                ? "true"
                                                : "false"
                                              : cellText}
                                          </td>
                                        );
                                      }
                                      return null;
                                    })}
                                    <td className="px-6 py-3 whitespace-nowrap">
                                      <div className="flex items-center gap-3">
                                        <SquarePen
                                          size={16}
                                          className="text-primaryBlue hover:text-muted-foreground cursor-pointer transition"
                                          onClick={() =>
                                            handleEdit(pageUid, index)
                                          }
                                        />
                                        <Trash
                                          size={16}
                                          className="text-primary hover:text-muted-foreground cursor-pointer transition"
                                          onClick={() =>
                                            setPendingDelete({ pageUid, index })
                                          }
                                        />
                                      </div>
                                    </td>
                                  </tr>
                                );
                              },
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* <div className="flex justify-end gap-4 mt-4 m-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleClosePreview}
                    >
                      Close
                    </Button>
                  </div> */}
                  </div>
                </TabsContent>
              );
            })}

            {helpPages?.map((page) => (
              <TabsContent
                key={page.uid}
                value={String(page.uid)}
                className="space-y-4 h-[65vh] overflow-y-auto mt-3 bg-[white] px-3 py-2 border border-secondary rounded"
              >
                <div className="prose max-w-none">
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        page?.helpContent?.trim() || page?.content?.trim()
                          ? page?.helpContent?.trim() ||
                            page?.content?.trim() ||
                            "<p>No help content provided.</p>"
                          : "<p>No help content provided.</p>",
                    }}
                  />
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UiPreview;
